import { CheckIcon, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { BpCard } from '../../../../components/BpCard/BpCard';
import { WorkMaterialsList } from '../../../../components/WorkMaterialsList/WorkMaterialsList';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { niceDate } from '../../../../utils/dateCalculations';
import { OpenAssignmentsOverview } from '../../../../components/OpenAssignmentsOverview/OpenAssignmentsOverview';
import { AnnouncementsList } from '../../../../components/AnnouncementsList/AnnouncementsList';
import { useGetMatrixRoomByName } from '../../../../hooks/matrix/useGetMatrixRoomByName';
import { RoomNames } from '../../../../utils/matrixClient';
import { useGetAnnouncements } from '../../../../hooks/matrix/useGetAnnouncements';
import { NotificationCountType } from 'matrix-js-sdk';
import { useDays } from '../../../../hooks/useDays';
import { GroupType, SortDirection } from '@bp/bp-graphql-types';
import { graphql } from 'client/gql';
import { useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';

// eslint-disable-next-line graphql-variables/mandatory
const CourseOverviewStudentContentQueryDocument = graphql(`
  query CourseOverviewStudentContentData(
    $groupWhere: GroupWhere!
    $groupOptions: GroupOptions
    $workMaterialsOptions: WorkMaterialOptions
    $currentCourseWhere: GroupWhere!
    $currentCourseOptions: GroupOptions
    $profile: ID!
    $calendarEventWhere: CalendarEventWhere!
  ) {
    ...OpenAssignmentQueryFields
    ...PermissionCheckerFields
    groups(where: $groupWhere, options: $groupOptions) {
      uuid
      __typename
      teachingUnits {
        uuid
        __typename
        title
        workMaterials(options: $workMaterialsOptions) {
          uuid
          __typename
          title
          updated
          visibleFrom {
            uuid
            __typename
            date
          }
          visibleTo {
            uuid
            __typename
            date
          }
          ...WorkMaterialsListFields
        }
      }
    }
    currentCourse: groups(where: $currentCourseWhere, options: $currentCourseOptions) {
      uuid
      __typename
      organization {
        uuid
        __typename
      }
      subject {
        uuid
        __typename
        organization {
          uuid
          __typename
        }
      }
    }
  }
`);

type CourseOverviewStudentContentProps = {
  courseUuid: string;
};

export const CourseOverviewStudentContent = ({ courseUuid }: CourseOverviewStudentContentProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: CourseOverviewStudentContentQueryDocument,
    variables: {
      profile: profileUuid,
      groupWhere: {
        uuid: courseUuid,
      },
      workMaterialsOptions: {
        sort: [
          {
            updated: SortDirection.Asc,
          },
        ],
      },
      currentCourseWhere: { uuid: courseUuid ?? '', type: GroupType.Course },
      currentCourseOptions: {
        limit: 1,
      },
      calendarEventWhere: courseUuid
        ? {
            holderConnection: {
              Group: {
                node: {
                  uuid: courseUuid,
                },
              },
            },
          }
        : {
            organization: {
              uuid: organizationUuid,
            },
            attendees_SOME: {
              uuid: pimAuthClaims.getProfile().uuid,
            },
          },
    },
  });

  const perms = usePermissionChecker(data);

  const navigate = useNavigate();

  const { markAsRead } = useMatrixClient();
  const { getRoom } = useGetMatrixRoomByName();

  const currentCourse = data?.currentCourse?.[0];

  const room = getRoom(`${currentCourse?.organization.uuid}_${courseUuid}`, RoomNames.Group);
  const hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;
  const announcements = useGetAnnouncements(room);

  const groupSubject = { uuid: courseUuid ?? '', organization: { uuid: currentCourse?.organization.uuid ?? '' } };

  const materials = data?.groups
    ?.map((g) => {
      return g.teachingUnits.map((t) =>
        t.workMaterials.map((item) => {
          return {
            ...item,
            timeFrame: `${niceDate(item.visibleFrom?.date, 'short')} - ${niceDate(item.visibleTo?.date, 'short')}`,
            holder: { uuid: t.uuid, title: t.title },
          };
        }),
      );
    })
    .flat(2);

  const { daysLeft } = useDays();

  const activeMaterials = materials?.filter((a) => {
    const daysDiffVisibleTo: number = daysLeft(a.visibleTo?.date, true);
    const daysDiffVisibleFrom: number = daysLeft(a.visibleFrom?.date, true);
    return daysDiffVisibleFrom < 1 && daysDiffVisibleTo > -1;
  });

  return (
    <Grid>
      <GridRow>
        <BpCard noPadding>
          <OpenAssignmentsOverview
            query={data}
            onNavigate={(courseUuid, uuid) => navigate(`/courses/${courseUuid}/assignments/${uuid}`)}
            courseUuid={courseUuid}
          />
        </BpCard>
      </GridRow>
      <GridRow>
        <GridColumn width={6}>
          <BpCard
            fixedHeight='374px'
            noPadding
            header={{
              headline: t('workmaterials.title'),
            }}
          >
            <WorkMaterialsList
              onNavigate={(uuid) => navigate(`/courses/${courseUuid}/workmaterials/${uuid}`)}
              workMaterial={activeMaterials}
              isGroupEditor={currentCourse?.subject ? (perms?.isCourseEditor(currentCourse.subject) ?? false) : false}
            />
          </BpCard>
        </GridColumn>
        <GridColumn width={6}>
          <BpCard
            canScroll
            fixedHeight='374px'
            noPadding
            header={{
              headline: t('announcements.title'),
              actions: hasUnread
                ? [
                    {
                      text: t('common.mark'),
                      hierarchy: 'ghost',
                      icon: <CheckIcon />,
                      callback: async () => {
                        await markAsRead(room);
                      },
                    },
                  ]
                : [],
            }}
          >
            <AnnouncementsList
              announcements={announcements}
              hasPermissionToDelete={perms?.canCreateCourseAnnouncement(groupSubject) ?? false}
            />
          </BpCard>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
